// Native agendy — VÝKONNÉ REGISTRAČNÍ METODY nad byznys definicemi (`nativeAgendaDefinitions.ts`).
//
// Účel (žádná byznys data ani typy — ty žijí vedle):
//  - getAllNativeAgendaRegistrations(): plochý dedup seznam VŠECH native agend pro GLOBÁLNÍ
//    registraci v kernelu (agenda jde otevřít jako osamostatněné (floating) okno bez návštěvy route
//    a přežije reload / deep-link);
//  - getNativeRootAgenda(path): překlad cesty menu → root agenda (otevření z menu/historie/našeptávače);
//  - installObjectCategoryNav(kernel): naplní neutrální registr `@/shared/lib/objectCategoryNav`
//    kernelem svázanými `openDetail`/`openList` closures → navigace daných ObjectCategory jdou na native.
//
// Rozdělení dle účelu: TYPY v nativeAgendaTypes · DATA v nativeAgendaDefinitions · LOGIKA zde.
// Doplněk k `routeContracts` (skutečné routes) — native agendy vlastní route nemají.

import type { AgendaRegistration, NavigationKernel } from '@/shared/navigation';
import {
  registerObjectCategoryNav,
  type ObjectCategorySourceLayerId,
} from '@/shared/lib/objectCategoryNav';

import type { AnyAgenda } from './nativeAgendaTypes';
import { getNativeAgendaModules, getNativeObjectCategoryAgendas } from './nativeAgendaDefinitions';

/** Plochý seznam VŠECH native agend (dedup dle agendaId) — pro globální registraci v kernelu. */
export function getAllNativeAgendaRegistrations(): AgendaRegistration[] {
  const byId = new Map<string, AgendaRegistration>();
  for (const moduleEntry of getNativeAgendaModules()) {
    for (const reg of moduleEntry.agendas) {
      if (!byId.has(reg.agenda.id)) byId.set(reg.agenda.id, reg);
    }
  }
  return [...byId.values()];
}

/** Root agenda native modulu dle cesty menu, nebo `undefined` (path není native modul s agendou). */
export function getNativeRootAgenda(path: string): AnyAgenda | undefined {
  return getNativeAgendaModules().find((m) => m.path === path)?.rootAgenda;
}

/**
 * Native agenda (root i modální/detailová) dle jejího `agendaId` — pro znovuotevření z historie,
 * kde je uložený stabilní `agendaId` (ne cesta menu). Hledá napříč všemi registracemi.
 */
export function findNativeAgendaById(agendaId: string): AnyAgenda | undefined {
  return getAllNativeAgendaRegistrations().find((reg) => reg.agenda.id === agendaId)?.agenda as
    | AnyAgenda
    | undefined;
}

/** Přenese floating stack zdrojové vrstvy na nově otevřenou vrchní vrstvu (zrcadlí `inheritSourceStack`). */
function inheritStack(
  kernel: NavigationKernel,
  sourceLayerId: ObjectCategorySourceLayerId | undefined
): void {
  if (!sourceLayerId) return;
  const source = kernel.getLayer(sourceLayerId);
  if (!source?.stackId) return;
  const newTopId = kernel.getState().activeTopLayerId;
  if (newTopId && newTopId !== sourceLayerId) {
    kernel.setLayerStackId(newTopId, source.stackId);
  }
}

/**
 * Nainstaluje mapování „ObjectCategory → native agenda" (z `nativeAgendaDefinitions`) do neutrálního
 * registru (`@/shared/lib/objectCategoryNav`). Volá se ze shell page (má kernel). Poté se navigace
 * daných ObjectCategory přesměrují na native agendy místo legacy Magicu.
 */
export function installObjectCategoryNav(kernel: NavigationKernel): void {
  for (const item of getNativeObjectCategoryAgendas()) {
    registerObjectCategoryNav(item.objectCategory, {
      picker: item.picker,
      openDetail: (id, sourceLayerId) => {
        void kernel
          .openModal(item.detail, item.toDetailParams(id))
          .then(() => inheritStack(kernel, sourceLayerId));
      },
      openList: item.list
        ? (sourceLayerId) => {
            void kernel
              .openModal(item.list as AnyAgenda, {})
              .then(() => inheritStack(kernel, sourceLayerId));
          }
        : undefined,
    });
  }
}
